import React, { memo, useCallback, useEffect, useState } from 'react';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Flag, { FlagGroup } from '@atlaskit/flag';
import StatusError from '@atlaskit/icon/core/status-error';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ContextualAnalyticsData, fireUIAnalytics, MODAL } from '@atlassian/analytics-bridge';

import messages from './messages';
import { cleanupRovoWidgetSDKScript, loadRovoWidgetSDKScript } from './utils';

const InjectRovoServiceAgentWidgetScript = memo(function RovoServiceAgentWidget({
    siteId,
    widgetId,
}: {
    siteId: string;
    widgetId: string;
}) {
    di(loadRovoWidgetSDKScript, fireUIAnalytics, useAnalyticsEvents, UFOLoadHold);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleOnOpen = useCallback(() => {
        // https://data-portal.internal.atlassian.com/analytics/registry/88552
        fireUIAnalytics(createAnalyticsEvent({}), 'rovoServiceAgentWidget opened');
    }, [createAnalyticsEvent]);

    const handleOnClose = useCallback(() => {
        // https://data-portal.internal.atlassian.com/analytics/registry/88553
        fireUIAnalytics(createAnalyticsEvent({}), 'rovoServiceAgentWidget closed');
    }, [createAnalyticsEvent]);

    useEffect(() => {
        const loadScript = async () => {
            try {
                setIsLoading(true);
                setError(null);
                await loadRovoWidgetSDKScript({ siteId, widgetId, onOpen: handleOnOpen, onClose: handleOnClose });
            } catch (err) {
                setError(err instanceof Error ? err : new Error('Failed to load widget script'));
            }
        };

        loadScript().finally(() => {
            setIsLoading(false);
        });
    }, [widgetId, siteId, handleOnClose, handleOnOpen]);

    useEffect(() => {
        return () => {
            cleanupRovoWidgetSDKScript();
        };
    }, []);

    if (error) {
        throw error;
    }

    if (isLoading) {
        return <UFOLoadHold name="rovo-widget-loader" />;
    }

    return null;
});

export const RovoServiceAgentWidgetInjector = ({ siteId, widgetId }: { siteId: string; widgetId: string }) => {
    di(ErrorBoundary, UFOSegment, ContextualAnalyticsData);

    const { formatMessage } = useIntl();
    const renderError = useCallback(() => {
        return (
            <FlagGroup>
                <Flag
                    id="rovo-widget-error-flag"
                    appearance="error"
                    icon={<StatusError label="" />}
                    title={formatMessage(messages.widgetErrorTitle)}
                    description={formatMessage(messages.widgetErrorDescription)}
                    testId="rovo-widget-error-flag"
                />
            </FlagGroup>
        );
    }, [formatMessage]);

    return (
        <UFOSegment name="rovo-service-agent-widget">
            <ErrorBoundary packageName="rovo-service-agent" id="widget-injector" renderError={renderError}>
                <ContextualAnalyticsData
                    sourceName="rovoServiceAgent"
                    sourceType={MODAL}
                    attributes={{ siteId, widgetId }}
                >
                    <InjectRovoServiceAgentWidgetScript siteId={siteId} widgetId={widgetId} />
                </ContextualAnalyticsData>
            </ErrorBoundary>
        </UFOSegment>
    );
};
